import type { Config } from './types';

export const DEFAULTS: Config = {
  // `null`, not `''`: unconfigured is not the same as same-origin (#51).
  api: null,
  // `sub1`/`sub2` are the canonical page passthroughs (#226); `sub_id2` stays
  // as the legacy alias of sub1 so existing pages keep working unchanged.
  params: ['campaign', 'sub_id2', 'sub1', 'sub2'],
  timeout: 800,
  target: '_blank',
  debug: false,
  imgHosts: null,
};

let debugOn = false;

/** Debug logging (opt-in via data-mdfs-debug / MDFS_CONFIG.debug). Never throws. */
export function log(...args: unknown[]): void {
  if (debugOn) {
    try {
      console.log('[mdfs]', ...args);
    } catch {
      /* a page that broke console is not our problem */
    }
  }
}

// Comma string or array → clean token list. `__proto__` is dropped: param names
// end up as payload keys, and assigning that one would hit the prototype setter.
function ok(s: string): boolean {
  return !!s && s !== '__proto__';
}

function list(v: unknown): string[] | null {
  if (Array.isArray(v)) return v.map(String).filter(ok);
  if (typeof v === 'string' && v) return v.split(',').map((s) => s.trim()).filter(ok);
  return null;
}

function num(v: unknown): number | null {
  const n = typeof v === 'number' ? v : parseInt(String(v), 10);
  return isFinite(n) && n > 0 ? n : null;
}

function bool(v: unknown): boolean | null {
  if (v === true || v === 'true' || v === '' || v === '1') return true;
  if (v === false || v === 'false' || v === '0') return false;
  return null;
}

function text(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

// Precedence in one helper. Written out rather than with `??` on purpose: the
// ES2018 target downlevels nullish chains into a much larger expression, and
// this file would otherwise cost ~250 gzipped bytes of the 3 KB budget.
function first<T>(a: T | null | undefined, b: T | null | undefined, dflt: T): T {
  return a != null ? a : b != null ? b : dflt;
}

/**
 * Resolve config. Precedence, most specific first (documented in README):
 *
 *   1. `data-mdfs-*` attributes on the test element (timeout only) — applied per request in mdfs.ts
 *   2. `data-mdfs-*` attributes on the `<script>` tag that loaded the snippet
 *   3. `window.MDFS_CONFIG` (site-wide object, must be set before the snippet runs)
 *   4. built-in defaults
 */
export function resolveConfig(script: Element | null, global: unknown): Config {
  const g = (global && typeof global === 'object' ? global : {}) as Record<string, unknown>;
  const attr = (name: string): string | null =>
    script ? script.getAttribute('data-mdfs-' + name) : null;

  // An absent attribute *and* an absent global leave this null, which makes no
  // request at all; `data-mdfs-api=""` is how a page asks for same origin.
  const api = first(attr('api'), text(g.api), DEFAULTS.api);

  const cfg: Config = {
    api: api && api.replace(/\/+$/, ''),
    params: first(list(attr('params')), list(g.params), DEFAULTS.params),
    timeout: first(num(attr('timeout')), num(g.timeout), DEFAULTS.timeout),
    target: first(attr('target'), text(g.target), DEFAULTS.target),
    debug: first(bool(attr('debug')), bool(g.debug), DEFAULTS.debug),
    imgHosts: first(list(attr('img-hosts')), list(g.imgHosts), DEFAULTS.imgHosts),
  };
  debugOn = cfg.debug;
  return cfg;
}
